import { Injectable } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class OidcStorageService implements AbstractSecurityStorage {
  private readonly encKey = CryptoJS.enc.Hex.parse('WedecoUVSystemXGSAuthKeyData2025');
  private readonly encIv = CryptoJS.enc.Hex.parse('5202ataDyeKhtuASGXmetsySVUocedew');
  private readonly writtenKeys = new Set<string>();

  read(key: string): string {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const jsonValue = JSON.parse(raw);
      if (!jsonValue) return null;
      for (const prop of Object.keys(jsonValue)) {
        if (prop === 'authnResult') {
          for (const result in jsonValue[prop]) {
            if (jsonValue[prop][result] && result.includes('token')) {
              const dec = this.tryDecrypt(jsonValue[prop][result]);
              if (dec === null) { localStorage.removeItem(key); return null; }
              jsonValue[prop][result] = dec;
            }
          }
        } else if (prop === 'userData' || prop === 'authzData') {
          const dec = this.tryDecrypt(jsonValue[prop]);
          if (dec === null) { localStorage.removeItem(key); return null; }
          jsonValue[prop] = JSON.parse(dec);
        }
      }
      return JSON.stringify(jsonValue);
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }

  write(key: string, value: string): void {
    try {
      this.writtenKeys.add(key);
      const jsonValue = JSON.parse(value);
      const properties = Object.keys(jsonValue);
      for (const prop of properties) {
        if (prop === 'authnResult') {
          for (const result in jsonValue[prop]) {
            if (jsonValue[prop][result] && result.includes('token')) {
              jsonValue[prop][result] = this.encrypt(jsonValue[prop][result]);
            }
          }
        } else if (prop === 'userData' || prop === 'authzData') {
          jsonValue[prop] = this.encrypt(JSON.stringify(jsonValue[prop]));
        }
      }
      localStorage.setItem(key, JSON.stringify(jsonValue));
    } catch { /* ignore */ }
  }

  remove(key: string): void {
    localStorage.removeItem(key);
    this.writtenKeys.delete(key);
  }

  clear(): void {
    this.writtenKeys.forEach(k => localStorage.removeItem(k));
    this.writtenKeys.clear();
  }

  private encrypt(data: string): string {
    return CryptoJS.AES.encrypt(data, this.encKey, {
      iv: this.encIv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    }).toString();
  }

  private tryDecrypt(data: string): string | null {
    try {
      const result = CryptoJS.AES.decrypt(data, this.encKey, {
        iv: this.encIv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }).toString(CryptoJS.enc.Utf8);
      return result || null;
    } catch {
      return null;
    }
  }
}
